import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Link, Stack, SvgIcon, Typography } from '@mui/material';

import { CreditCardIcon, MplInformationMessage } from '@mopla/ui';
import ProfileOutlined from '@mopla/ui/icons/ProfileOutlined.svg';

interface IUserInfoContentProps {
	ticketHolder: string;
	tariffConditionsUrl?: string;
	isYearlyTicket: boolean;
}

export const UserInfoContent: FC<IUserInfoContentProps> = ({
	ticketHolder,
	tariffConditionsUrl,
	isYearlyTicket,
}) => {
	const { t } = useTranslation('ticketshop');

	return (
		<Stack gap={1} sx={{ width: '100%', alignItems: 'flex-start' }}>
			<Stack
				direction="row"
				alignItems="center"
				gap={1}
				sx={{ width: '100%', minHeight: 42 }}
			>
				<Box sx={{ display: 'flex' }}>
					<SvgIcon
						component={ProfileOutlined}
						color="primary"
						sx={{ fontSize: 24 }}
					/>
				</Box>
				<Stack gap={'1px'} sx={{ flex: 1, minWidth: 0 }}>
					<Typography variant="b2-m" color="primary.main">
						{ticketHolder}
					</Typography>
					<Typography variant="l1" color="solutions.600">
						{t('ticket_detail.user_info.holder_hint')}
					</Typography>
				</Stack>
			</Stack>

			{isYearlyTicket ? (
				<>
					<Stack direction="row" alignItems="flex-start" gap={1}>
						<Box>
							<SvgIcon
								component={CreditCardIcon}
								color="primary"
								fontSize="medium"
							/>
						</Box>
						<Typography variant="l1" color="primary.main">
							{t('ticket_detail.user_info.yearly_physical_card_info')}
						</Typography>
					</Stack>
					<MplInformationMessage alignItems="flex-start">
						{t('ticket_detail.user_info.yearly_weekends_info')}
					</MplInformationMessage>
					<MplInformationMessage alignItems="flex-start">
						{t('ticket_detail.user_info.non_transferrable_info')}
					</MplInformationMessage>
				</>
			) : (
				<MplInformationMessage>
					{t('ticket_detail.user_info.single_passenger_warning')}
				</MplInformationMessage>
			)}

			{tariffConditionsUrl && (
				<Box sx={{ pl: 4, width: '100%' }}>
					<Link
						href={tariffConditionsUrl}
						target="_blank"
						rel="noopener noreferrer"
						underline="always"
						sx={{
							color: (theme) => theme.palette.primary.main,
							textDecorationColor: (theme) => theme.palette.primary.main,
							typography: 'l1',
						}}
					>
						{t('ticket_detail.user_info.more_contract_details')}
					</Link>
				</Box>
			)}
		</Stack>
	);
};
